import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Award, ShieldCheck } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import { meApi } from "@/lib/api";

export default function CertificatesPage() {
    const [certs, setCerts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        meApi
            .certificates()
            .then(setCerts)
            .catch(() => setCerts([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="bg-brand-offwhite" data-testid="certificates-page">
            <section className="border-b border-brand-line py-20 md:py-28">
                <div className="container-x">
                    <Reveal>
                        <h1 className="font-display text-6xl leading-[1.05] md:text-8xl">
                            YOUR <span className="text-brand-gold">CERTIFICATES.</span>
                        </h1>
                        <p className="mt-6 max-w-2xl font-body text-lg leading-relaxed text-brand-steel">
                            Every completed course generates a verifiable Certificate of Completion. Each certificate carries a unique reference and a public verification URL.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="py-20 md:py-24">
                <div className="container-x">
                    {loading ? (
                        <p
                            className="font-mono-tag text-brand-steel"
                            data-testid="certificates-loading"
                        >
                            LOADING CERTIFICATES…
                        </p>
                    ) : certs.length === 0 ? (
                        <div
                            className="mx-auto max-w-xl border border-dashed border-brand-line bg-white p-12 text-center"
                            data-testid="certificates-empty"
                        >
                            <Award className="mx-auto h-10 w-10 text-brand-gold" />
                            <p className="mt-6 font-display text-3xl uppercase">
                                No certificates yet
                            </p>
                            <p className="mt-3 font-body text-brand-steel">
                                Complete a course to earn your first certificate.
                            </p>
                            <Link
                                to="/portal/courses"
                                className="mt-8 inline-flex btn-primary"
                                data-testid="certificates-empty-cta"
                            >
                                Browse courses
                                <ArrowUpRight className="h-4 w-4" />
                            </Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {certs.map((c, i) => (
                                <Reveal
                                    key={c.id}
                                    delay={i * 0.05}
                                    className="group flex flex-col border border-brand-line bg-white"
                                >
                                    <div
                                        className="flex flex-col p-6"
                                        data-testid={`certificate-card-${c.id}`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <Award className="h-8 w-8 text-brand-gold" />
                                            <span className="font-mono-tag text-brand-steel">
                                                {c.cert_number}
                                            </span>
                                        </div>
                                        <h3 className="mt-6 font-display text-2xl uppercase leading-tight">
                                            {c.course_title}
                                        </h3>
                                        <p className="mt-2 font-mono-tag text-brand-steel">
                                            Awarded to {c.holder_name}
                                        </p>
                                        <p className="mt-1 font-mono-tag text-brand-steel">
                                            Issued {new Date(c.issued_at).toLocaleDateString(
                                                "en-GB",
                                                {
                                                    day: "numeric",
                                                    month: "short",
                                                    year: "numeric",
                                                },
                                            )}
                                        </p>
                                        <div className="mt-6 flex items-center gap-2 border-t border-brand-line pt-4 font-mono-tag text-brand-gold">
                                            <ShieldCheck className="h-3 w-3" />
                                            Publicly verifiable
                                        </div>
                                        <div className="mt-6 flex flex-wrap gap-3">
                                            <Link
                                                to={`/portal/certificates/${c.id}`}
                                                className="btn-primary"
                                                data-testid={`certificate-view-${c.id}`}
                                            >
                                                View
                                                <ArrowUpRight className="h-4 w-4" />
                                            </Link>
                                        </div>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
